import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Helper to get stored token
const getStoredToken = () => localStorage.getItem('session_token');
const setStoredToken = (token) => {
  if (token) localStorage.setItem('session_token', token);
  else localStorage.removeItem('session_token');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = getStoredToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        setStoredToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, sessionToken) => {
    setUser(userData);
    if (sessionToken) setStoredToken(sessionToken);
  };

  const logout = async () => {
    try {
      const token = getStoredToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setStoredToken(null);
  };

  const loginWithGoogle = () => {
    // Redirect to auth callback page which will handle the session
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const adminLogin = async (password, email = null) => {
    // If email is provided, use email + password login
    // Otherwise, use shared admin password
    const body = email 
      ? { email: email.toLowerCase(), password }
      : { password };
    
    const response = await fetch(`${API}/auth/admin-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Invalid credentials');
    }
    
    const data = await response.json();
    setUser(data.user);
    if (data.session_token) setStoredToken(data.session_token);
    return data.user;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    loginWithGoogle,
    adminLogin,
    checkAuth,
    isAdmin: user?.role === 'admin',
    isMember: user?.role === 'member' || user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
